// Need to use the React-specific entry point to allow generating React hooks
import { IAddCourseDto } from "@backend/dto/addCourseDto";
import { baseAPI } from "./baseAPI";
import { getApiKeyHeaders } from "./apiUtils";
import type {
  ICourse,
  ICourseWithInvitation,
  ICourseInvitation,
} from "@backend/model/course";
import { IMessage } from "@backend/model/message";
import { IGalleryUrl } from "@backend/model/gallery";

const enchancedAPI = baseAPI.enhanceEndpoints({
  addTagTypes: ["courses", "invitations", "messages", "images"],
});

// Define a service using a base URL and expected endpoints
export const coursesAPI = enchancedAPI.injectEndpoints({
  endpoints: (builder) => ({
    listCourses: builder.query<ICourse[], string>({
      query: (organisationId: string) =>
        `organisation/${organisationId}/courses`,
      providesTags: ["courses"],
    }),
    createCourse: builder.mutation<
      ICourse,
      { course: IAddCourseDto; organisationId: string }
    >({
      query: ({ course, organisationId }) => ({
        url: `organisation/${organisationId}/courses`,
        method: "POST",
        body: course,
      }),
      invalidatesTags: (res) => (res ? ["courses"] : []),
    }),
    updateCourse: builder.mutation<
      ICourse,
      { course: IAddCourseDto; organisationId: string; courseId: string }
    >({
      query: ({ course, organisationId, courseId }) => ({
        url: `organisation/${organisationId}/courses/${courseId}`,
        method: "PUT",
        body: course,
      }),
      invalidatesTags: (res) => (res ? ["courses", "images"] : []),
    }),
    completeCourse: builder.mutation<
      any,
      {
        organisationId: string;
        courseId: string;
        userId: string;
        apiKey?: string;
        password?: string;
        targetUserId?: string;
      }
    >({
      query: (props) => {
        const { organisationId, courseId, userId, apiKey, targetUserId } =
          props;
        const bodyString = targetUserId ? targetUserId : userId;
        return {
          url: `organisation/${organisationId}/courses/${courseId}/complete`,
          headers: apiKey && userId ? getApiKeyHeaders(props) : undefined,
          method: "POST",
          body: { userId: bodyString },
        };
      },
      invalidatesTags: (res) => (res ? ["invitations"] : []),
    }),
    getCourseById: builder.query<
      ICourse,
      {
        organisationId: string;
        courseId: string;
        userId?: string;
        apiKey?: string;
        password?: string;
      }
    >({
      query: (props) => {
        const { organisationId, courseId, userId, apiKey } = props;
        return {
          url: `organisation/${organisationId}/courses/${courseId}`,
          headers: apiKey && userId ? getApiKeyHeaders(props) : undefined,
        };
      },
      providesTags: ["courses"],
    }),
    getInvitations: builder.query<
      ICourseInvitation[],
      {
        organisationId: string;
        courseId: string;
      }
    >({
      query: (props) => {
        const { organisationId, courseId} = props;
        return {
          url: `organisation/${organisationId}/courses/${courseId}/invitations`,
        };
      },
      providesTags: ["invitations"],
    }),
    getCourseLink: builder.query<
      { url: string },
      { organisationId: string; courseId: string; userId: string }
    >({
      query: ({ organisationId, courseId, userId }) =>
        `organisation/${organisationId}/courses/${courseId}/link?userId=${userId}`,
    }),
    getCourseInformationByUserId: builder.query<
      ICourseWithInvitation[],
      { organisationId: string; userId: string }
    >({
      query: ({ organisationId, userId }) =>
        `organisation/${organisationId}/role/${userId}/invitations`,
      providesTags: ["invitations"],
    }),
    inviteToCourse: builder.mutation<
      any,
      { organisationId: string; courseId: string; userIds: string[] }
    >({
      query: ({ organisationId, courseId, userIds }) => {
        return {
          url: `organisation/${organisationId}/courses/${courseId}/invitations`,
          method: "POST",
          body: { userIds, test: "sdf" },
        };
      },
      invalidatesTags: (res) => (res ? ["invitations"] : []),
    }),
    deleteCourse: builder.mutation<any, any>({
      query: ({ organisationId, courseId }) => {
        return {
          url: `organisation/${organisationId}/courses/${courseId}/delete`,
          method: "DELETE",
        };
      },
      invalidatesTags: (res) => (res ? ["courses", "invitations"] : []),
    }),
    listMessages: builder.query<IMessage[], any>({
      query: ({ organisationId, courseId }) =>
        `organisation/${organisationId}/courses/${courseId}/messages`,
      providesTags: ["messages"],
    }),
    addMessage: builder.mutation<
      IMessage,
      {
        message: IMessage;
        organisationId: string;
        courseId: string;
        userId?: string;
        apiKey?: string;
        password?: string;
      }
    >({
      query: ({
        message,
        organisationId,
        courseId,
        userId,
        apiKey,
        password,
      }) => {
        return {
          url: `organisation/${organisationId}/courses/${courseId}/messages`,
          method: "POST",
          body: message,
          headers:
            apiKey && userId
              ? getApiKeyHeaders({
                  organisationId,
                  courseId,
                  userId,
                  apiKey,
                  password,
                })
              : undefined,
        };
      },
      invalidatesTags: (res) => (res ? ["messages"] : []),
    }),
    listImages: builder.query<IGalleryUrl[], string>({
      query: (organisationId: string) =>
        `organisation/${organisationId}/images`,
      providesTags: ["images"],
    }),
    listOrganisationImages: builder.query<IGalleryUrl[], string>({
      query: (organisationId: string) =>
        `organisation/${organisationId}/images/${organisationId}`,
      providesTags: ["images"],
    }),
    deleteImage: builder.mutation<any, any>({
      query: ({ organisationId, imageName }) => {
        return {
          url: `organisation/${organisationId}/images/${imageName}/delete`,
          method: "DELETE",
        };
      },
      invalidatesTags: (res) => (res ? ["images"] : ["images"]),
    }),
    checkUserInvitation: builder.mutation<
      {userId: string},
      {
        organisationId: string;
        courseId: string;
        userId?: string;
        username: string;
        apiKey?: string;
        password?: string;
      }
    >({
      query: ({ organisationId, courseId, userId, apiKey, password, username }) => {
        return {
          url: `organisation/${organisationId}/courses/${courseId}/checkuser/`,
          method: "POST",
          body: {username: username},
          headers:
            apiKey && userId
              ? getApiKeyHeaders({
                  organisationId,
                  courseId,
                  userId,
                  apiKey,
                  password,
                })
              : undefined,
        };
      },
      invalidatesTags: (res) => (res ? ["invitations"] : []),
    }),
  }),
});

export const {
  useListCoursesQuery,
  useGetCourseInformationByUserIdQuery,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useGetCourseByIdQuery,
  useGetInvitationsQuery,
  useCompleteCourseMutation,
  useInviteToCourseMutation,
  useLazyGetCourseLinkQuery,
  useDeleteCourseMutation,
  useListMessagesQuery,
  useAddMessageMutation,
  useListImagesQuery,
  useListOrganisationImagesQuery,
  useDeleteImageMutation,
  useCheckUserInvitationMutation,
} = coursesAPI;
